import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import ReactDOM from "react-dom";

/**
 * PublicWordBubble — the tap-a-word popup on the shared-topic page.
 *
 * Slim read-only sibling of TopicDetail's WordBubble: definition + play +
 * info (opens the word-detail panel) + word structure when the tapped
 * occurrence has morphology (◇, same as the in-app bubble). No word bank,
 * no admin annotation controls. Positioning/close behaviour mirrors the
 * original so the two feel identical.
 */
export default function PublicWordBubble({ word, anchorRect, onInfoClick, onClose, onPlay, isPlaying, hasStructure = false, onStructureClick }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null); // null = invisible until measured

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    document.addEventListener("scroll", onClose, true); // close on any scroll
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  // Measure after render, then clamp to the viewport (top-based positioning
  // only — window.innerHeight is unreliable on mobile).
  const reposition = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const W = el.offsetWidth;
    const H = el.offsetHeight;
    const MARGIN = 8;
    const centerX = anchorRect.left + anchorRect.width / 2;
    const left = Math.max(MARGIN, Math.min(window.innerWidth - W - MARGIN, centerX - W / 2));
    const showAbove = anchorRect.top > H + 20;
    setPos({ left, showAbove, top: showAbove ? anchorRect.top - H - 10 : anchorRect.bottom + 10 });
  }, [anchorRect]);

  useLayoutEffect(() => { reposition(); }, [reposition]);

  const definition = (word.senses?.find((s) => s.is_primary)?.definition)
    || word.conjugation_info?.definition
    || word.senses?.[0]?.definition
    || "No definition";

  const posStyle = pos
    ? { position: "fixed", left: pos.left, top: pos.top, zIndex: 9999 }
    : { position: "fixed", visibility: "hidden", zIndex: 9999 };

  return ReactDOM.createPortal(
    <div ref={ref} style={posStyle} className="bg-slate-800 text-white rounded-lg shadow-2xl px-3 py-2 max-w-[280px] min-w-[100px]">
      <div className="flex items-center gap-2">
        <p className="flex-1 text-sm leading-snug whitespace-normal">{definition}</p>
        <button
          type="button"
          title={isPlaying ? "Stop" : "Play"}
          onClick={() => onPlay(word)}
          className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-emerald-400 transition-colors"
        >
          {isPlaying ? (
            <svg className="w-4 h-4 text-emerald-400" viewBox="0 0 24 24" fill="currentColor">
              <rect x="5" y="5" width="14" height="14" rx="2" />
            </svg>
          ) : (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 4l14 8-14 8V4z" />
            </svg>
          )}
        </button>
        <button
          type="button"
          title="Full word detail"
          onClick={() => { onInfoClick(); onClose(); }}
          className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-emerald-400 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
        </button>
        {/* Word structure (morphology blow-up) — shown only when this
            occurrence carries structure data, mirroring the in-app bubble. */}
        {hasStructure && onStructureClick && (
          <button
            type="button"
            title="View word structure"
            aria-label="View word structure"
            onClick={() => { onStructureClick(); onClose(); }}
            className="shrink-0 p-0.5 rounded-full text-slate-400 hover:text-emerald-400 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l9 9-9 9-9-9 9-9z" />
            </svg>
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
